import React, { useState, useRef, useEffect } from "react";
import Sidebar from "./Sidebar";
import { useLocation } from "react-router-dom";
import { User, LogOut } from "lucide-react";
import api from "@/lib/api";

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const getPageTitle = () => {
    if (location.pathname.startsWith("/dashboard")) return "Admin Dashboard";
    if (location.pathname.startsWith("/cars")) return "Manage Cars";
    if (location.pathname.startsWith("/bookings")) return "Manage Bookings";
    if (location.pathname.startsWith("/users")) return "Manage Users";
    if (location.pathname.startsWith("/notifications")) return "Notifications";
    if (location.pathname.startsWith("/admin/edit-region-rates"))
      return "Edit Region Rates";
    if (location.pathname.startsWith("/edit")) return "Edit Website";
    if (location.pathname.startsWith("/audit")) return "System Audit"; // ← added
    return "Admin Panel";
  };

  const handleLogout = async () => {
    try {
      await api.post("/logout");
    } catch {
      // even if the call fails, continue with client cleanup
    } finally {
      localStorage.clear();
      sessionStorage.clear();
      window.location.replace("/login");
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex h-screen">
      <Sidebar />

      <div className="flex-1 mt-2 overflow-y-auto bg-gray-100">
        {/* Header */}
        <div className="sticky top-0 z-20 px-4">
          <div className="relative flex items-center justify-between px-8 py-4 text-white bg-blue-900 rounded-md">
            <div className="w-8 h-8" />
            <h1 className="absolute text-2xl font-extrabold transform -translate-x-1/2 left-1/2">
              {getPageTitle()}
            </h1>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setOpenMenu(!openMenu)}
                className="flex items-center justify-center w-8 h-8 text-white bg-yellow-400 rounded-full"
              >
                <User className="w-5 h-5" />
              </button>

              {openMenu && (
                <div className="absolute right-0 z-10 w-48 mt-2 bg-white border rounded shadow">
                  <div className="px-4 py-2 text-sm font-medium text-gray-700">
                    Logged in as <br />
                    <span className="font-semibold">
                      {user?.email || "Unknown"}
                    </span>
                  </div>

                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main content */}
        <main className="p-6">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
